import _ from 'lodash/collection';

import keysData from '../data/keys.json';
import timeSignaturesData from '../data/timeSignatures.json';
import { normalizeKeyDisplay } from '../utils/diatonicChords';
import { getSongChartCoverage } from '../utils/partCharts';
import { getSetting, settingKeys } from '../utils/settings';

/**
 * Options for the Sort by section of the songs filter modal
 */
export const SONG_SORT_OPTIONS = [
  { value: 'none', label: 'Default' },
  { value: 'most', label: 'Most Played' },
  { value: 'least', label: 'Least Played' },
  { value: 'newest', label: 'Newest Added to Service' },
  { value: 'oldest', label: 'Oldest Added to Service' },
  { value: 'updated', label: 'Recently Updated' },
];

const CHROMATIC_ROOTS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/**
 * Strips the alternative half of an enharmonic key, so "C#/Db" and "C#" both key off "C#".
 * This is the same reduction the songs list filters by, and the counts have to agree with
 * it — otherwise a song saved as plain "C#" is selected by the "C#/Db" option without ever
 * having been counted in it.
 * @param {string} key - The key to normalize
 * @returns {string} - The key root
 */
const keyRoot = (key) => key.replace(/\/.*$/, '');

/**
 * Counts how many songs are set to each key
 * @param {Array} songs - The songs to count
 * @returns {Object} - Map of key root to song count
 */
const countSongsByKey = (songs) => {
  const counts = {};

  songs.forEach((song) => {
    if (song.key) {
      const root = keyRoot(song.key);
      counts[root] = (counts[root] || 0) + 1;
    }
  });

  return counts;
};

const chromaticIndex = (key) => CHROMATIC_ROOTS.indexOf(keyRoot(key).replace(/m$/, ''));

/**
 * Builds the key chips for the filter modal, labelled with the user's sharp/flat preference
 * and counted against the songs passed in
 * @param {Array} songs - The songs to count by key
 * @returns {{ major: Array, minor: Array }} - Chips ({ value, label, count }) in chromatic order
 */
export const getKeyFilterOptions = (songs = []) => {
  const preferSharps = getSetting(settingKeys.preferSharps) !== false; // Default to true
  const keyCounts = countSongsByKey(songs);
  const toOption = (key) => ({
    value: key,
    label: normalizeKeyDisplay(key, preferSharps),
    count: keyCounts[keyRoot(key)] || 0,
  });
  const byPitch = (a, b) => chromaticIndex(a) - chromaticIndex(b);
  const isMinor = (key) => keyRoot(key).endsWith('m');

  return {
    major: keysData
      .filter((key) => !isMinor(key))
      .sort(byPitch)
      .map(toOption),
    minor: keysData.filter(isMinor).sort(byPitch).map(toOption),
  };
};

/**
 * Whether a song is in one of the selected keys. No selection matches every song.
 * @param {Object} song - The song to test
 * @param {Array} selectedKeys - Keys picked in the filter
 * @returns {boolean}
 */
export const matchesKeys = (song, selectedKeys = []) => {
  if (selectedKeys.length === 0) return true;
  if (!song.key) return false;

  return selectedKeys.some((key) => keyRoot(key) === keyRoot(song.key));
};

const timeSignatureOf = (song) => song.timeSignature || '4/4';

/**
 * Builds the time signature chips for the filter modal, in the order the song form offers them
 * @param {Array} songs - The songs to count by time signature
 * @returns {Array} - Chips ({ value, label, count })
 */
export const getTimeSignatureFilterOptions = (songs = []) => {
  const counts = {};

  songs.forEach((song) => {
    const signature = timeSignatureOf(song);
    counts[signature] = (counts[signature] || 0) + 1;
  });

  return timeSignaturesData.map((signature) => ({ value: signature, label: signature, count: counts[signature] || 0 }));
};

/**
 * Whether a song is in one of the selected time signatures. No selection matches every song.
 * @param {Object} song - The song to test
 * @param {Array} selected - Time signatures picked in the filter
 * @returns {boolean}
 */
export const matchesTimeSignatures = (song, selected = []) =>
  selected.length === 0 || selected.includes(timeSignatureOf(song));

/**
 * Builds the theme chips for the filter modal, in the team's theme order
 * @param {Array} songs - The songs to count by theme
 * @param {Array} allThemes - Every theme the team has used
 * @returns {Array} - Chips ({ value, label, count })
 */
export const getThemeFilterOptions = (songs = [], allThemes = []) => {
  const counts = {};

  songs.forEach((song) => {
    (song.themes || []).forEach((theme) => {
      counts[theme] = (counts[theme] || 0) + 1;
    });
  });

  return [...new Set(allThemes)].map((theme) => ({ value: theme, label: theme, count: counts[theme] || 0 }));
};

/**
 * Whether a song carries any of the selected themes. No selection matches every song.
 * @param {Object} song - The song to test
 * @param {Array} selected - Themes picked in the filter
 * @returns {boolean}
 */
export const matchesThemes = (song, selected = []) =>
  selected.length === 0 || (song.themes || []).some((theme) => selected.includes(theme));

const CHART_STATUSES = [
  { value: 'full', label: 'Fully charted' },
  { value: 'partial', label: 'Partly charted' },
  { value: 'none', label: 'Not charted' },
];

/**
 * The share of a song's chartable parts that carry a chart, from 0 to 1. A song with nothing
 * chartable (only notes or headings) counts as 0, not as fully charted.
 * @param {Object} song - The song
 * @returns {number}
 */
export const chartedShare = (song) => {
  const coverage = getSongChartCoverage(song);

  return coverage?.total > 0 ? coverage.charted / coverage.total : 0;
};

/**
 * How charted a song is: 'full', 'partial', or null when no part carries a chart
 * @param {Object} song - The song
 * @returns {string|null}
 */
export const chartStatusOf = (song) => {
  const share = chartedShare(song);

  if (share >= 1) return 'full';
  return share > 0 ? 'partial' : null;
};

/**
 * Builds the chart chips for the filter modal
 * @param {Array} songs - The songs to count by chart status
 * @returns {Array} - Chips ({ value, label, count })
 */
export const getChartFilterOptions = (songs = []) => {
  const counts = {};

  songs.forEach((song) => {
    const status = chartStatusOf(song) || 'none';
    counts[status] = (counts[status] || 0) + 1;
  });

  return CHART_STATUSES.map((status) => ({ ...status, count: counts[status.value] || 0 }));
};

/**
 * Whether a song is charted as far as one of the selected statuses, 'none' for a song with no chart
 * at all. No selection matches every song.
 * @param {Object} song - The song to test
 * @param {Array} selected - Statuses picked in the filter
 * @returns {boolean}
 */
export const matchesCharts = (song, selected = []) =>
  selected.length === 0 || selected.includes(chartStatusOf(song) || 'none');

/**
 * Orders songs fully charted first, then the rest by how much of them is charted. Stable, so
 * songs charted alike keep the order the chosen sort gave them.
 * @param {Array} songs - Songs already in their sort order
 * @returns {Array}
 */
export const orderByCharted = (songs = []) => _.orderBy(songs, [chartedShare], ['desc']);

/**
 * Service titles are free text, so "Sunday" and "sunday " have to count as one name
 * @param {string} title - A service title
 * @returns {string} - The name services are grouped by
 */
export const normalizeServiceName = (title = '') => title.trim().replace(/\s+/g, ' ').toLowerCase();

const toTime = (date) => (date ? new Date(date).getTime() || 0 : 0);

// When a song was added to a service. createdAt rather than the service date, which is often
// days ahead and matches what "Added to Sunday 3 days ago" means.
const serviceAddedDate = (service) => service.createdAt || service.date;

/**
 * Every service name the team has used, most recent first. Songs' lastServicePlay is read
 * too, so a name survives its services being deleted.
 * @param {Array} services - The team's services
 * @param {Array} songs - The team's songs
 * @returns {Array} - Options ({ id, label, lastUsed })
 */
export const getServiceNameOptions = (services = [], songs = []) => {
  const names = new Map();

  const add = (title, date) => {
    const id = normalizeServiceName(title);
    if (!id) return;

    const time = toTime(date);
    const current = names.get(id);

    if (!current || time > current.lastUsed) names.set(id, { id, label: title.trim(), lastUsed: time });
  };

  services.forEach((service) => add(service.title, serviceAddedDate(service)));
  songs.forEach((song) => {
    const { serviceTitle, date } = song.lastServicePlay || {};

    if (serviceTitle) add(serviceTitle, date);
  });

  return [...names.values()].sort((a, b) => b.lastUsed - a.lastUsed);
};

/**
 * Indexes the services each song has been in
 * @param {Array} services - The team's services
 * @returns {Map} - songId → [{ serviceTitle, name, date }]
 */
export const buildServicePlaysBySong = (services = []) => {
  const plays = new Map();

  services.forEach((service) => {
    const play = {
      serviceTitle: service.title,
      name: normalizeServiceName(service.title),
      date: serviceAddedDate(service),
    };

    service.songsIds?.forEach((songId) => {
      if (!plays.has(songId)) plays.set(songId, []);
      plays.get(songId).push(play);
    });
  });

  return plays;
};

/**
 * The last time a song was added to a service whose name is still ticked. With nothing
 * unticked it is the song's own lastServicePlay, untouched.
 * @param {Object} song - The song
 * @param {Map} playsBySong - From buildServicePlaysBySong
 * @param {Array} excludedNames - Normalized service names unticked in the filter
 * @returns {Object|null} - { serviceTitle, date }, or null if never added to a ticked service
 */
export const resolveServicePlay = (song, playsBySong, excludedNames = []) => {
  const last = song.lastServicePlay?.serviceTitle ? song.lastServicePlay : null;

  if (excludedNames.length === 0) return last;

  const excluded = new Set(excludedNames);
  const candidates = (playsBySong?.get(song._id) || []).filter((play) => !excluded.has(play.name));

  if (last && !excluded.has(normalizeServiceName(last.serviceTitle))) candidates.push(last);
  if (candidates.length === 0) return null;

  const latest = candidates.reduce((best, play) => (toTime(play.date) > toTime(best.date) ? play : best));

  return { serviceTitle: latest.serviceTitle, date: latest.date };
};
